import React, {useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@meridian/components/column";
import Text from "@amzn/meridian/text";
import {AslDeviceOverviewContext} from "../context/AslDeviceOverviewContext";
import Alert from "@amzn/meridian/alert"
import {
    PORT_MASK,
    isValidEntry,
    IP_ADDRESS_PATTERN,
    PATTERNS, validateNewAslDevice, PROTOCOLS, MAC_MASK,
} from "../../../util/Validations";
import {assetTypeToConfigTypeDict} from "../../../util/Util";

const AddAslSingleDeviceModal = (props) => {

    const {formatMessage} = props.intl;
    const {open, setOpen, selected, setSelected} = props;
    const {viewModel, dispatch} = useContext(AslDeviceOverviewContext);

    // Visual controls
    const [showAlert, setShowAlert] = useState(false);

    // States
    const [port, setPort] = useState("");
    const [deviceType, setDeviceType] = useState("");
    const [name, setName] = useState("");
    const [ip, setIp] = useState("");
    const [mac, setMac] = useState("");
    const [id, setId] = useState("");

    // Error states
    const [portErr, setPortErr] = useState(false);
    const [deviceTypeErr, setDeviceTypeErr] = useState(false);
    const [nameErr, setNameErr] = useState(false);
    const [ipErr, setIpErr] = useState(false);
    const [macErr, setMacErr] = useState(false);
    const [idErr, setIdErr] = useState(false);

    const stateValues = {
        mac: mac,
        ip: ip,
        name: name,
        deviceType: deviceType,
        port: port,
        deviceConfig: {
            type: assetTypeToConfigTypeDict.typeASL,
            id: id
        }
    }

    const onCloseModal = () => {
        setOpen(false)
        resetInputState();
        setShowAlert(false);
    }

    const onClickConfirm = (stateValues) => {
        if (validateNewAslDevice(stateValues)) {
            setShowAlert(false);
            stateValues.deviceConfig = JSON.stringify(stateValues.deviceConfig)
            if (selected) {
                stateValues.divertConfig = JSON.stringify(viewModel.aslDeviceInformation[stateValues.mac].divertConfig);
                updateData(stateValues);
            } else {
                addData(stateValues);
            }
            setOpen(false);
            resetInputState();
        } else {
            setShowAlert(true);
        }
    };

    function resetInputState() {
        setMac("");
        setMacErr(false);
        resetAssetDetails();
        clearAssetDetailErrors();
    }

    function resetAssetDetails() {
        setIp("");
        setId("");
        setPort("");
        setDeviceType("");
        setName("");
    }

    function clearAssetDetailErrors() {
        setIpErr(false);
        setIdErr(false);
        setPortErr(false);
        setDeviceTypeErr(false);
        setNameErr(false);
    }

    const prefill = (value) => {
        if (viewModel.aslDeviceInformation.hasOwnProperty(value)) {
            setSelected(value);
            const device = viewModel.aslDeviceInformation[value]
            clearAssetDetailErrors();
            setMac(device.mac)
            setIp(device.ip);
            setId(device.id);
            setPort(device.port);
            setDeviceType(device.deviceType);
            setName(device.name);
        } else if (selected) {
            setSelected(false);
            resetAssetDetails()
        }
    }

    const addData = (stateValues) => {
        dispatch({type: 'ADD_DEVICE_DATA', data: {dispatcher: dispatch, stateValues: stateValues}})
    }
    const updateData = (stateValues) => {
        dispatch({type: 'UPDATE_DEVICE_DATA', data: {dispatcher: dispatch, stateValues: stateValues}})
    }

    return (
        <Modal
            title={formatMessage({id: "device.add_device_button_modal"})}
            open={open}
            onOpen={() => prefill(selected)}
            onClose={onCloseModal}
            scrollContainer="viewport"
            closeLabel="Close"
            bodySpacingInset="medium"
            width="200%">
            <Column spacing={"medium"} spacingInset={"medium"}>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="mac-input">
                        <FormattedMessage id="modal.mac" defaultMessage="MAC Address"/>
                    </Text>
                    <Input id="mac-input"
                        value={mac}
                        onChange={setMac}
                        mask={MAC_MASK}
                        onBlur={() => {
                            setMacErr(!isValidEntry(stateValues.mac, PATTERNS.MAC));
                            prefill(mac);
                        }}
                        error={macErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="id-input">
                        <FormattedMessage id="modal.ip" defaultMessage="IP Address"/>
                    </Text>
                    <Input id="ip-input"
                        value={ip}
                        onChange={setIp}
                        onBlur={() => setIpErr(!isValidEntry(stateValues.ip, IP_ADDRESS_PATTERN))}
                        error={ipErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="port-input">
                        <FormattedMessage id="modal.port" defaultMessage="Port"/>
                    </Text>
                    <Input id="port-input"
                        value={port}
                        onChange={setPort}
                        mask={PORT_MASK}
                        onBlur={() => setPortErr(!isValidEntry(stateValues.port, PATTERNS.PORT))}
                        error={portErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        <FormattedMessage id="modal.protocol"
                                          defaultMessage="Protocol"/>
                    </Text>
                    <Input id="device-type-input"
                        value={deviceType}
                        onChange={setDeviceType}
                        onBlur={() =>
                            setDeviceTypeErr(!PROTOCOLS.ASL.includes(stateValues.deviceType)
                                && stateValues.deviceType !== '')
                        }
                        error={deviceTypeErr}
                        helperText={"Either \"slam\" or \"slam-2021-v1\""}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="name-input">
                        <FormattedMessage id="modal.name"
                                          defaultMessage="Device Name"/>
                    </Text>
                    <Input id="name-input"
                        value={name}
                        onChange={setName}
                        onBlur={() => setNameErr(!isValidEntry(stateValues.name, PATTERNS.NAME))}
                        error={nameErr}
                        helperText={"Display name of the device. e.g. \"Line 3\""}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="id-input">
                        <FormattedMessage id="modal.device_id"
                                          defaultMessage="Device ID"/>
                    </Text>
                    <Input id="id-input"
                        value={id}
                        onChange={setId}
                        onBlur={() => setIdErr(!isValidEntry(stateValues.deviceConfig.id, PATTERNS.DEVICE_ID))}
                        error={idErr}
                        helperText={"Unique local identifier for the device. eg. \"1\" for \"Line 1\""}
                    />
                </Column>
            </Column>
            <ModalFooter>
                <Column>
                    {showAlert && <Alert type="warning" size="small">
                            <FormattedMessage id="alert.missingfields"
                                              defaultMessage="Please enter all fields in correct format!"/>
                        </Alert>}
                </Column>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" size="medium" onClick={onCloseModal}>
                        <FormattedMessage id="modal.cancel" defaultMessage="Cancel"/>
                    </Button>
                    <Button type="primary" size="medium"
                            onClick={() => onClickConfirm(stateValues)}>
                        {selected ? <FormattedMessage id="modal.confirmEdit" defaultMessage="Confirm edit"/> :
                            <FormattedMessage id="modal.confirmAdd" defaultMessage="Confirm add"/>
                        }
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    )
}

export default injectIntl(AddAslSingleDeviceModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/asldevice/modal/AddAslSingleDeviceModal.js